import React, { useState, useEffect } from "react";
import validator from "validator";
import { useCreateRoleMutation, useUpdateRoleMutation } from "../../../redux/api";

import {
  Button,
  Form,
  Container,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { toast } from "react-toastify";

const PermissionsModal = (props) => {
  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  
  const [rolesErr, setErr] = useState({});
  const [permissions, setPermissions] = useState(props.role);
  const [totalRoles, setTotalRoles] = useState(0);

  // Sync internal permissions state with props.role
  useEffect(() => {
    if (props.role) {
      setPermissions(props.role);
      
      // Calculate total roles count for select all logic
      let count = 0;
      Object.keys(props.role).forEach((key) => {
         if (key !== "status" && key !== "title" && key !== "superAdminRole" && key !== "_id" && key !== "id") {
             count++;
         }
      });
      setTotalRoles(count);
    }
  }, [props.role]);

  const onChangeCheckbox = (name, value) => {
    let newPermissions = { ...permissions };
    
    if (name === "selectAll") {
      Object.keys(newPermissions).forEach((key) => {
        if (key !== "status" && key !== "title" && key !== "superAdminRole" && key !== "_id" && key !== "id") {
             newPermissions[key] = value;
        }
      });
      props.setSelectAll(value);
      // Update parent state as well if needed, but props.role seems to be the source
      // Actually prop.role is passed down, but is it updated? 
      // Listing passes 'role' which is state. 'setModal' updates it.
      // But typically 'role' prop is strictly from parent. 
      // The parent probably passed 'setRole' implicitly via 'setModal' logic? 
      // Wait, looking at listing:
      // <PermissionsModal ... role={role} ... />
      // But it doesn't pass 'setRole'. 
      // It seems the modal was managing local state 'permissions' in the commented code?
      // Yes, "const [permissions, setPermissions] = useState(props.role || ...)"
      
    } else {
      newPermissions[name] = value;

      // Select all logic
      let count = 0;
      let total = 0; 
      // Recalculate based on current state
       Object.keys(newPermissions).forEach((key) => {
         if (key !== "status" && key !== "title" && key !== "superAdminRole" && key !== "_id" && key !== "id") {
             total++;
             if (newPermissions[key] === true) count++;
         }
      });
      
      let allSelected = count === total;
      props.setSelectAll(allSelected);
    }
    setPermissions(newPermissions);
    
    // We might need to bubble this up if parent needs it, but usage suggests create/update happens here.
    // Listing has 'role' state but it seems mainly for initial data?
  };

  const submit = async (e) => {
    e.preventDefault();
    let err = {};
    if (!props.title || !props.title.trim()) {
      err.title = "Title Required.";
    }

    let hasOnePermission = false;
    for (let key in permissions) {
      if (permissions[key] === true) hasOnePermission = true;
    }

    if (!hasOnePermission) {
      err.roles = "Please check at least one permission.";
      toast.error(err.roles);
    }

    if (Object.keys(err).length > 0) {
        setErr(err);
        return;
    }
    
    // Prepare payload
    // The backend expects 'permissions' as a JSON string or object? 
    // Legacy implementation sent: title, status, permissions (JSON string usually)
    // Let's assume the API handles it. The Mutation takes body.
    
    const payload = {
        title: props.title,
        status: props.status,
        permissions: permissions
    };
    
    if (props.modalType === 1) { // Add
        try {
            const res = await createRole(payload).unwrap();
            toast.success(res.message || "Role created successfully");
            props.setData(initialRole); // Reset or Refresh
            onCloseHandler();
        } catch (error) {
            toast.error(error?.data?.message || "Failed to create role");
        }
    } else if (props.modalType === 3) { // Edit
        try {
            const updatePayload = {
                id: props.id,
                ...payload
            };
            const res = await updateRole(updatePayload).unwrap();
            toast.success(res.message || "Role updated successfully");
             // Refresh parent list is automatic via cache invalidation?
             // Listing uses useGetRolesQuery(qs). If mutation invalidates 'Roles', it refreshes.
             // We need to check if tags are set. Assuming yes.
             onCloseHandler();
             // Maybe call a prop to trigger refetch if needed, but RTK Query handles it.
             // props.setData(res.data) might be needed if parent keys off it.
             props.setData(res.data); // This calls refetch() in parent based on earlier reading.
        } catch (error) {
            toast.error(error?.data?.message || "Failed to update role");
        }
    }
  };

  const onCloseHandler = () => {
    props.setroleModal(false);
    setErr({});
    props.setModalType(0);
  };
  
    // Initial Role structure for reset
    const initialRole = {
    viewDashboard: false,
    addStaff: false, editStaff: false, deleteStaff: false, viewStaff: false,
    addRole: false, editRole: false, deleteRole: false, viewRole: false,
    addCategory: false, editCategory: false, deleteCategory: false, viewCategory: false,
    viewContent: false, addContent: false, editContent: false, deleteContent: false,
    addDocument: false, deleteDocument: false, viewDocument: false,
    addEvent: false, editEvent: false, deleteEvent: false, viewEvent: false,
    addGallery: false, deleteGallery: false, viewGallery: false,
    viewSetting: false, editSetting: false,
    addTeamMember: false, editTeamMember: false, deleteTeamMember: false, viewTeamMember: false,
    addSlider: false, editSlider: false, deleteSlider: false, viewSlider: false,
    viewAbout: false, editAbout: false,
    status: true,
  };


  return (
    <Container fluid>
      {props.modalType > 0 && (
        <Modal
          className="modal-primary"
          onHide={onCloseHandler}
          show={props.roleModal}
          size="lg"
        >
          <Modal.Header className="justify-content-center">
            <Row>
              <div className="col-12">
                <h4 className="mb-0 mb-md-3 mt-0">
                  {props.modalType === 1
                    ? "Add New"
                    : props.modalType === 2
                    ? "View"
                    : "Edit"}{" "}
                  Staff Role
                </h4>
              </div>
            </Row>
          </Modal.Header>
          <Modal.Body className="modal-body">
            <Form onSubmit={submit}>
              <Form.Group>
                <Row>
                  <Col md={12}>
                    <label className="label-font">
                      Title <span className="text-danger">*</span>
                    </label>
                    <Form.Control
                      className="mb-3"
                      placeholder="Enter title"
                      type="text"
                      name="title"
                      onChange={(e) => props.setTitle(e.target.value)}
                      disabled={props.modalType === 2}
                      value={props.title}
                      required
                    />
                    <div className="d-flex justify-content-between align-items-center edit-title">
                      <span>
                        <label
                          className={
                            rolesErr.title ? `pl-1 text-danger` : `d-none`
                          }
                        >
                          {rolesErr.title}
                        </label>
                      </span>
                      <label className="right-label-checkbox">
                        Select All
                        <input
                          type="checkbox"
                          name="selectAll"
                          disabled={props.modalType === 2}
                          onChange={(e) =>
                            onChangeCheckbox("selectAll", !props.selectAll)
                          }
                          checked={props.selectAll}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </div>
                  </Col>
                </Row>

              </Form.Group>



              {/* Group: Dashboard */}
              <PermissionGroup
                label="Dashboard"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewDashboard", label: "View" }
                ]}
              />
              
              {/* Group: Staff */}
               <PermissionGroup
                label="Staff"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewStaff", label: "View" },
                    { name: "addStaff", label: "Add" },
                    { name: "editStaff", label: "Edit" },
                    { name: "deleteStaff", label: "Delete" },
                ]}
              />

              {/* Group: Role */}
               <PermissionGroup
                label="Staff Roles"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewRole", label: "View" },
                    { name: "addRole", label: "Add" },
                    { name: "editRole", label: "Edit" },
                    { name: "deleteRole", label: "Delete" },
                ]}
              />

             {/* Group: Category */}
               <PermissionGroup
                label="Category"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewCategory", label: "View" },
                    { name: "addCategory", label: "Add" },
                    { name: "editCategory", label: "Edit" },
                    { name: "deleteCategory", label: "Delete" },
                ]}
              />

             {/* Group: Content */}
               <PermissionGroup
                label="Content"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewContent", label: "View" },
                    { name: "addContent", label: "Add" },
                    { name: "editContent", label: "Edit" },
                    { name: "deleteContent", label: "Delete" },
                ]}
              />

             {/* Group: Document */}
               <PermissionGroup
                label="Document"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewDocument", label: "View" },
                    { name: "addDocument", label: "Add" },
                    { name: "deleteDocument", label: "Delete" },
                ]}
              />

             {/* Group: Event */}
               <PermissionGroup
                label="Event"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewEvent", label: "View" },
                    { name: "addEvent", label: "Add" },
                    { name: "editEvent", label: "Edit" },
                    { name: "deleteEvent", label: "Delete" },
                ]}
              />

             {/* Group: Gallery */}
               <PermissionGroup
                label="Gallery"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewGallery", label: "View" },
                    { name: "addGallery", label: "Add" },
                    { name: "deleteGallery", label: "Delete" },
                ]}
              />

             {/* Group: Team Member */}
               <PermissionGroup
                label="Team Member"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewTeamMember", label: "View" },
                    { name: "addTeamMember", label: "Add" },
                    { name: "editTeamMember", label: "Edit" },
                    { name: "deleteTeamMember", label: "Delete" },
                ]}
              />

             {/* Group: Slider */}
               <PermissionGroup
                label="Slider"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewSlider", label: "View" },
                    { name: "addSlider", label: "Add" },
                    { name: "editSlider", label: "Edit" },
                    { name: "deleteSlider", label: "Delete" },
                ]}
              />
              
              {/* Group: About */}
               <PermissionGroup
                label="About"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewAbout", label: "View" },
                    { name: "editAbout", label: "Edit" },
                ]}
              />

               {/* Group: Settings */}
               <PermissionGroup
                label="Settings"
                modalType={props.modalType}
                permissions={permissions}
                onChange={onChangeCheckbox}
                items={[
                    { name: "viewSetting", label: "View" },
                    { name: "editSetting", label: "Edit" },
                ]}
              />

              <Form.Group>
                <Row>
                  <Col md={12}>
                    <label className="label-font">Status</label>
                    <div className="d-flex align-items-center mt-2">
                       <label className="right-label-radio mr-3 mb-2">
                        Active
                        <input
                          name="status"
                          type="radio"
                          disabled={props.modalType === 2}
                          checked={props.status}
                          value={true}
                          onChange={(e) => props.setStatus(true)}
                        />
                        <span className="checkmark"></span>
                      </label>
                      <label className="right-label-radio mr-3 mb-2">
                        Inactive
                        <input
                          name="status"
                          type="radio"
                          disabled={props.modalType === 2}
                          checked={!props.status}
                          value={false}
                          onChange={(e) => props.setStatus(false)}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </div>
                  </Col>
                </Row>
              </Form.Group>

              <div className="d-flex justify-content-end p-2 border-top">
                    <Button variant="danger" onClick={onCloseHandler} className="outline-button mr-2">
                        Close
                    </Button>
                    {props.modalType !== 2 && (
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isCreating || isUpdating}
                            className="btn-filled"
                        >
                            {(isCreating || isUpdating) ? "Saving..." : "Save"}
                        </Button>
                    )}
              </div>

            </Form>
          </Modal.Body>
        </Modal>
      )}
    </Container>
  );
};

// Helper component to dry up the code
const PermissionGroup = ({ label, modalType, permissions, onChange, items }) => {
    return (
        <Form.Group>
            <Row>
                <Col md={4}>
                    <label className="label-font">{label}</label>
                </Col>
                <Col md={8} className="check-inline d-flex flex-wrap">
                    {items.map((item) => (
                        <label key={item.name} className="right-label-checkbox mr-3 mb-2">
                            {item.label}
                            <input
                                type="checkbox"
                                name={item.name}
                                disabled={modalType === 2}
                                onChange={(e) => onChange(item.name, !permissions[item.name])}
                                checked={!!permissions[item.name]}
                            />
                            <span className="checkmark"></span>
                        </label>
                    ))}
                </Col>
            </Row>
        </Form.Group>
    );
};

export default PermissionsModal;
